/**
 * The platform console.
 *
 * A different product from Club Office, living in the same bundle: this is the
 * screen the platform's owner uses to onboard customers, set what they are
 * charged and see what the business earned. It has its own sign-in and its own
 * token, and it is deliberately reachable only at /platform.
 *
 * The one design decision worth stating: commission is shown as money, not as
 * a rate, wherever there is a choice. "2.5%" is what was agreed; "$418 this
 * month" is the thing being decided about.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  platformApi, platformToken, type PlatformClub, type PlatformRevenue,
} from '../lib/platform-api';
import {
  Card, CheckField, Empty, ErrorNote, Loading, Modal, Pill, Stat, Tabs,
  TextField, errorMessage, useToast,
} from '../components/ui';

const money = (cents: number, currency = 'CAD') =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(cents / 100);

export function PlatformPage() {
  const [signedIn, setSignedIn] = useState(() => Boolean(platformToken.get()));
  const [tab, setTab] = useState<'clubs' | 'revenue' | 'audit'>('clubs');

  if (!signedIn) return <PlatformLogin onDone={() => setSignedIn(true)} />;

  return (
    <div className="shell shell--plain">
      <div className="main">
        <div className="topbar">
          <div>
            <h1>Platform</h1>
            <div className="topbar__sub">Every club, and what each one earns</div>
          </div>
          <div className="topbar__actions">
            <button
              className="btn"
              type="button"
              onClick={() => { platformToken.clear(); setSignedIn(false); }}
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="content stack">
          <Tabs
            tabs={[
              { id: 'clubs', label: 'Clubs' },
              { id: 'revenue', label: 'Revenue' },
              { id: 'audit', label: 'Access log' },
            ] as const}
            active={tab}
            onChange={setTab}
          />
          {tab === 'clubs' && <Clubs />}
          {tab === 'revenue' && <Revenue />}
          {tab === 'audit' && <AuditLog />}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- sign in -- */

function PlatformLogin({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await platformApi.post<{ token: string }>('/auth/login', { email, password });
      platformToken.set(res.token);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <h1>Platform</h1>
        <p className="small">
          This is not the club sign-in. A Club Office account will not work here.
        </p>
        {error && <div className="banner banner--error" role="alert">{error}</div>}
        <TextField label="Email" value={email} onChange={setEmail} type="email" required />
        <TextField label="Password" value={password} onChange={setPassword} type="password" required />
        <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ clubs -- */

function Clubs() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PlatformClub | null>(null);

  const clubs = useQuery({
    queryKey: ['platform', 'clubs'],
    queryFn: () => platformApi.get<{ data: PlatformClub[] }>('/clubs').then((r) => r.data),
  });

  if (clubs.isLoading) return <Loading what="Reading the clubs" />;
  if (clubs.error) return <ErrorNote error={clubs.error} />;

  const list = clubs.data ?? [];

  return (
    <>
      <div className="grid grid--4">
        <Stat label="Clubs" value={list.length} />
        <Stat
          label="Taking cards"
          value={list.filter((c) => c.stripe.chargesEnabled).length}
          note="Stripe onboarding complete"
        />
        <Stat
          label="Not yet connected"
          value={list.filter((c) => !c.stripe.accountId).length}
          note="Can only take money at the club"
          alert={list.some((c) => !c.stripe.accountId)}
        />
        <Stat
          label="Commission earned"
          value={money(list.reduce((n, c) => n + c.commissionCents, 0))}
          note="All time"
        />
      </div>

      <Card
        title="Clubs"
        actions={
          <button className="btn btn--sm btn--primary" type="button" onClick={() => setAdding(true)}>
            Onboard a club
          </button>
        }
        flush
      >
        {list.length === 0 ? (
          <div className="card__body"><Empty title="No clubs yet" /></div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Club</th><th>Members</th><th>Stripe</th>
                <th>Taken</th><th>Commission</th><th>Terms</th><th />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <div className="small">{c.slug}</div>
                    {!c.isActive && <Pill tone="bad">suspended</Pill>}
                  </td>
                  <td>{c.memberCount}</td>
                  <td><StripePill club={c} /></td>
                  <td className="mono">{money(c.grossCents)}</td>
                  <td className="mono">{money(c.commissionCents)}</td>
                  <td className="small">
                    {c.commission.enabled
                      ? `${c.commission.percent}%${c.commission.fixedCents ? ` + ${money(c.commission.fixedCents)}` : ''}`
                      : <Pill tone="quiet">none</Pill>}
                  </td>
                  <td>
                    <button className="btn btn--sm" type="button" onClick={() => setEditing(c)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {adding && (
        <OnboardClub
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            void queryClient.invalidateQueries({ queryKey: ['platform'] });
          }}
        />
      )}
      {editing && (
        <EditClub
          club={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['platform'] });
          }}
        />
      )}
    </>
  );
}

/**
 * Stripe state in one word.
 *
 * Three states, not two: a club that has started onboarding and stopped
 * halfway looks identical to one that never started unless it is said, and it
 * is the one that needs a phone call.
 */
function StripePill({ club }: { club: PlatformClub }) {
  if (!club.stripe.accountId) return <Pill tone="quiet">not connected</Pill>;
  if (club.stripe.chargesEnabled) return <Pill tone="good">live</Pill>;
  if (club.stripe.detailsSubmitted) return <Pill tone="warn">under review</Pill>;
  return <Pill tone="warn">half done</Pill>;
}

function OnboardClub({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [percent, setPercent] = useState('2');

  const create = useMutation({
    mutationFn: () => platformApi.post<{ temporaryPassword?: string }>('/clubs', {
      name,
      slug: slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      adminEmail,
      adminFirstName: first,
      adminLastName: last,
      commissionPercent: Number(percent) || 0,
    }),
    onSuccess: (res) => {
      toast.success(
        res.temporaryPassword
          ? `Club created. Temporary password: ${res.temporaryPassword}`
          : 'Club created. The first administrator has been emailed their password.',
      );
      onDone();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      title="Onboard a club"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            type="button"
            disabled={!name.trim() || !adminEmail.trim() || !first.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Create club
          </button>
        </>
      }
    >
      <TextField label="Club name" value={name} onChange={setName} placeholder="Bowmanville Curling Club" />
      <TextField
        label="Short name"
        value={slug}
        onChange={setSlug}
        hint="Used in URLs and to identify the club at sign-in. Made from the name if left blank."
      />

      <h3 className="small" style={{ marginTop: '1rem' }}>Their first administrator</h3>
      <p className="small">
        Created with the club, because a club nobody can sign in to is not usable.
        They are emailed a temporary password and made to change it.
      </p>
      <TextField label="First name" value={first} onChange={setFirst} />
      <TextField label="Last name" value={last} onChange={setLast} />
      <TextField label="Email" value={adminEmail} onChange={setAdminEmail} type="email" />

      <h3 className="small" style={{ marginTop: '1rem' }}>Commission</h3>
      <TextField
        label="Percent of each online payment"
        value={percent}
        onChange={setPercent}
        type="number"
        hint="Taken only on payments through Stripe. Money taken at the club is never charged."
      />
    </Modal>
  );
}

function EditClub(
  { club, onClose, onDone }: { club: PlatformClub; onClose: () => void; onDone: () => void },
) {
  const toast = useToast();
  const [name, setName] = useState(club.name);
  const [active, setActive] = useState(club.isActive);
  const [enabled, setEnabled] = useState(club.commission.enabled);
  const [percent, setPercent] = useState(String(club.commission.percent));
  const [fixed, setFixed] = useState(String(club.commission.fixedCents));
  const [min, setMin] = useState(String(club.commission.minCents));
  const [cap, setCap] = useState(club.commission.capCents === null ? '' : String(club.commission.capCents));

  const save = useMutation({
    mutationFn: () => platformApi.patch(`/clubs/${club.id}`, {
      name,
      isActive: active,
      commissionEnabled: enabled,
      commissionPercent: Number(percent) || 0,
      commissionFixedCents: Number(fixed) || 0,
      commissionMinCents: Number(min) || 0,
      commissionCapCents: cap.trim() === '' ? null : Number(cap),
    }),
    onSuccess: () => { toast.success('Saved.'); onDone(); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const link = useMutation({
    mutationFn: () => platformApi.post<{ data: { url: string } }>(`/clubs/${club.id}/stripe/link`),
    onSuccess: (r) => {
      void navigator.clipboard?.writeText(r.data.url).catch(() => {});
      toast.success('Onboarding link copied. It is single use and expires shortly.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      title={club.name}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </button>
        </>
      }
    >
      <TextField label="Club name" value={name} onChange={setName} />
      <CheckField
        label="Active"
        checked={active}
        onChange={setActive}
        hint="Suspending a club stops its members signing in. Nothing is deleted."
      />

      <h3 className="small" style={{ marginTop: '1rem' }}>Stripe</h3>
      <p className="small">
        {club.stripe.accountId
          ? <>Account <span className="mono">{club.stripe.accountId}</span> · <StripePill club={club} /></>
          : 'Not connected. The club starts this itself from Club Office.'}
      </p>
      {club.stripe.accountId && (
        <button className="btn btn--sm" type="button" disabled={link.isPending}
          onClick={() => link.mutate()}>
          Copy an onboarding link
        </button>
      )}

      <h3 className="small" style={{ marginTop: '1rem' }}>Commission</h3>
      <CheckField label="Charge commission" checked={enabled} onChange={setEnabled} />
      <TextField label="Percent" value={percent} onChange={setPercent} type="number" />
      <TextField label="Fixed, in cents" value={fixed} onChange={setFixed} type="number" />
      <TextField
        label="Minimum, in cents"
        value={min}
        onChange={setMin}
        type="number"
        hint="Stops a small junior fee earning a fraction of a cent."
      />
      <TextField
        label="Cap, in cents"
        value={cap}
        onChange={setCap}
        type="number"
        hint="Blank for none. Keeps a large hall hire from taking a disproportionate cut."
      />
      <p className="small">
        Taken as the application fee on each Stripe payment. Money taken at the
        club never touches Stripe and is never charged.
      </p>
    </Modal>
  );
}

/* ---------------------------------------------------------------- revenue -- */

function Revenue() {
  const revenue = useQuery({
    queryKey: ['platform', 'revenue'],
    queryFn: () => platformApi.get<{ data: PlatformRevenue }>('/revenue').then((r) => r.data),
  });

  if (revenue.isLoading) return <Loading what="Adding it up" />;
  if (revenue.error) return <ErrorNote error={revenue.error} />;
  const d = revenue.data!;

  const peak = Math.max(1, ...d.byMonth.map((m) => m.commissionCents));

  return (
    <>
      <div className="grid grid--4">
        <Stat label="Commission earned" value={money(d.totals.commissionCents)} note="All time" />
        <Stat label="Processed for clubs" value={money(d.totals.grossCents)} note="Their money" />
        <Stat label="Payments" value={d.totals.payments} />
        <Stat
          label="Taken at the desk"
          value={d.totals.inPersonPayments}
          note="No commission — never touched Stripe"
        />
      </div>

      <Card title="By month">
        {d.byMonth.length === 0 ? <Empty title="No payments yet" /> : (
          <div style={{ display: 'grid', gap: '.4rem' }}>
            {d.byMonth.map((m) => (
              <div key={m.month} style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                <span className="mono small" style={{ width: '4.5rem' }}>{m.month}</span>
                {/* A bar, not a chart library: one series, twelve points. */}
                <span
                  style={{
                    height: 10,
                    borderRadius: 99,
                    background: 'var(--ice-500, #3ba4dd)',
                    width: `${Math.max(2, (m.commissionCents / peak) * 100)}%`,
                  }}
                  aria-hidden="true"
                />
                <span className="mono small">{money(m.commissionCents)}</span>
                <span className="small" style={{ color: 'var(--text-soft)' }}>
                  of {money(m.grossCents)} · {m.payments} payment{m.payments === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="By club" flush>
        <table className="table">
          <thead>
            <tr><th>Club</th><th>Payments</th><th>Processed</th><th>Commission</th></tr>
          </thead>
          <tbody>
            {d.byClub.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.payments}</td>
                <td className="mono">{money(c.grossCents)}</td>
                <td className="mono">{money(c.commissionCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------- audit log -- */

/**
 * Who at the platform looked at whose data.
 *
 * Kept because a club will eventually ask, and because the honest answer has
 * to be a list rather than an assurance.
 */
function AuditLog() {
  const log = useQuery({
    queryKey: ['platform', 'audit'],
    queryFn: () => platformApi.get<{
      data: Array<{ id: string; action: string; by: string; club: string | null; createdAt: string }>;
    }>('/audit').then((r) => r.data),
  });

  if (log.isLoading) return <Loading />;
  if (log.error) return <ErrorNote error={log.error} />;

  return (
    <Card title="Platform access log" flush>
      {log.data?.length === 0 ? (
        <div className="card__body"><Empty title="Nothing recorded yet" /></div>
      ) : (
        <table className="table">
          <thead><tr><th>When</th><th>Who</th><th>Club</th><th>Action</th></tr></thead>
          <tbody>
            {log.data?.map((r) => (
              <tr key={r.id}>
                <td className="mono small">{new Date(r.createdAt).toLocaleString()}</td>
                <td>{r.by}</td>
                <td>{r.club ?? '—'}</td>
                <td className="small">{r.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
